import i18n from '@/i18n';
import { EContractType, EContractTypeSearch } from '@/types/contract';
import {
	Availabilities,
	DistanceValue,
	LastUpdateValue,
} from '@/types/filters';
import { JobExperience } from '@/types/job';
import { CandidateOrigin } from '@/types/worker';

import type { LabelValue } from '@/types/common';

const t = (key: string, named?: Record<string, unknown>): string =>
	i18n.global.t(key, named as Record<string, unknown>) as string;

const tm = (key: string): unknown => i18n.global.tm(key);

export type FilterSelectOption = LabelValue & {
	disabled?: boolean;
	hidden?: boolean;
	selected?: boolean;
};

export const getTalentplugApplicantDegreeOptions = (): LabelValue[] => [
	{ label: t('applicantDegree.bepCap'), value: 'bepCap' },
	{ label: t('applicantDegree.bac'), value: 'bac' },
	{ label: t('applicantDegree.bacPro'), value: 'bacPro' },
	{ label: t('applicantDegree.bac+2'), value: 'bac+2' },
	{ label: t('applicantDegree.bac+3'), value: 'bac+3' },
	{ label: t('applicantDegree.bac+4'), value: 'bac+4' },
	{ label: t('applicantDegree.bac+5'), value: 'bac+5' },
	{ label: t('applicantDegree.bac+6'), value: 'bac+6' },
	{ label: t('applicantDegree.doctor'), value: 'doctor' },
	{ label: t('applicantDegree.researcher'), value: 'researcher' },
];

export const getJobExperienceOptions = (): LabelValue[] => [
	{ label: t('jobExperience.NONE'), value: JobExperience.NONE },
	{ label: t('jobExperience.DEBUTANT'), value: JobExperience.DEBUTANT },
	{ label: t('jobExperience.ADVANCED'), value: JobExperience.ADVANCED },
	{ label: t('jobExperience.EXPERT'), value: JobExperience.EXPERT },
];

export const getJobExperienceFilterOptions = (): LabelValue[] => [
	{ label: t('jobExperience.DEBUTANT'), value: JobExperience.DEBUTANT },
	{ label: t('jobExperience.ADVANCED'), value: JobExperience.ADVANCED },
	{ label: t('jobExperience.EXPERT'), value: JobExperience.EXPERT },
];

export const getContractTypeOptions = (): LabelValue[] =>
	Object.values(EContractType).map((contractType) => ({
		label: t(`contractType.${contractType}`),
		value: contractType,
	}));

export const getContractTypeSearchOptions = (): FilterSelectOption[] => {
	const contractTypes = Object.values(EContractTypeSearch).map(
		(contractType) => ({
			label: t(`mission.job.contractType.search.${contractType}`),
			value: contractType,
		})
	);
	return [
		{
			label: t('mission.job.contractType.search.title'),
			value: '',
			disabled: true,
			hidden: true,
		},
		...contractTypes,
	];
};

export const getDistanceOptions = (): LabelValue[] => [
	{
		label: t('distance.lessThan25Km'),
		value: DistanceValue.LESS_THAN_25_KM,
	},
	{
		label: t('distance.lessThan50Km'),
		value: DistanceValue.LESS_THAN_50_KM,
	},
	{
		label: t('distance.lessThan75Km'),
		value: DistanceValue.LESS_THAN_75_KM,
	},
	{
		label: t('distance.lessThan100Km'),
		value: DistanceValue.LESS_THAN_100_KM,
	},
	{
		label: t('distance.lessThan200Km'),
		value: DistanceValue.LESS_THAN_200_KM,
	},
	{ label: t('distance.any'), value: DistanceValue.ANY },
];

export const getMatchingDistanceOptions = (): LabelValue[] => [
	{ label: t('distance.lessThan5Km'), value: DistanceValue.LESS_THAN_5_KM },
	{ label: t('distance.lessThan10Km'), value: DistanceValue.LESS_THAN_10_KM },
	{ label: t('distance.lessThan15Km'), value: DistanceValue.LESS_THAN_15_KM },
	...getDistanceOptions(),
];

export const getLastUpdateOptions = (): LabelValue[] =>
	Object.values(LastUpdateValue).map((lastUpdate) => ({
		label: t(`lastUpdate.${lastUpdate}`),
		value: lastUpdate,
	}));

export const getAvailabilityOptions = (): LabelValue[] =>
	Object.values(Availabilities).map((availability) => ({
		label: t(`availability.${availability}`),
		value: availability,
	}));

export const getApplicationOriginOptions = (): LabelValue[] =>
	Object.values(CandidateOrigin)
		.map((origin) => ({ label: origin, value: origin }))
		.sort((originA, originB) => originA.value.localeCompare(originB.value));

export const getAgencyRoleOptions = (): LabelValue[] => [
	{ label: t('roles.collaborator'), value: 'collaborator' },
	{ label: t('roles.manager'), value: 'manager' },
];

export const getCompanyRoleOptions = (): LabelValue[] => [
	{ label: t('roles.company'), value: 'company' },
];

export const getNotificationLevelOptions = (): FilterSelectOption[] => [
	{
		label: t('form.notificationLevel'),
		value: '',
		disabled: true,
		selected: true,
	},
	{ label: '-', value: '' },
	{ label: t('page.notifications.level.info'), value: 'info' },
	{ label: t('page.notifications.level.warning'), value: 'warning' },
	{ label: t('page.notifications.level.alert'), value: 'alert' },
];

export const getNotificationTypeOptions = (
	isCompany: boolean
): FilterSelectOption[] => {
	const role = isCompany ? 'company' : 'agency';
	const types = tm(`page.profile.notifications.types.${role}`);
	const typeKeys =
		types && typeof types === 'object' ? Object.keys(types as object) : [];

	return [
		{
			label: t('form.notificationType'),
			value: '',
			disabled: true,
			selected: true,
		},
		{ label: '-', value: '' },
		...typeKeys.map((key) => ({
			label: t(`page.profile.notifications.types.${role}.${key}`),
			value: key,
		})),
	];
};
